import { useState } from "react";
import { Send, Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import emailjs from '@emailjs/browser'; // Import EmailJS

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface StyleObject {
  [key: string]: React.CSSProperties | any;
}

export function ContactUs() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false); // Loading state

  // Replace these with your actual EmailJS credentials
  const EMAILJS_SERVICE_ID = 'service_drnhmuh';
  const EMAILJS_TEMPLATE_ID = 'template_b7a40gr';
  const EMAILJS_PUBLIC_KEY = 'vmmlQrQLjVSvcX21w';

  // Initialize EmailJS
  emailjs.init(EMAILJS_PUBLIC_KEY);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Send email using EmailJS
      const response = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          to_email: 'your-email@example.com', // Your receiving email
          subject: `New Contact Form Message from ${formData.name}`,
          reply_to: formData.email,
        }
      );

      if (response.status === 200) {
        setFormData({ name: "", email: "", message: "" });
        alert("Message sent successfully! We will get back to you within 24 hours.");
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert("An error occurred. Please try again or contact us directly.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const socialIcons = [
    { Icon: Instagram, href: "https://www.instagram.com/astranova_2k26/" },
    { Icon: Linkedin, href: "https://www.linkedin.com/in/astranova-tech-symposium-052028352/" },
  ];

  // Get theme styles
  const getThemeStyles = (): StyleObject => {
    const isDarkMode =
      document.documentElement.classList.contains("dark") ||
      document.documentElement.getAttribute("data-theme") === "dark";

    return {
      wrapper: {
        backgroundColor: "var(--color-background)",
        color: "var(--color-text)",
        minHeight: "100vh",
        paddingTop: "120px",
        paddingBottom: "80px",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
      } as React.CSSProperties,
      container: {
        maxWidth: "1200px",
        width: "100%",
        margin: "0 auto",
        padding: "0 20px",
      } as React.CSSProperties,
      grid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "60px",
        alignItems: "flex-start",
      } as React.CSSProperties,
      leftColumn: {
        paddingTop: "20px",
        textAlign: "left",
      } as React.CSSProperties,
      title: {
        background:
          "linear-gradient(to right, var(--color-primary), var(--color-accent))",
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "var(--color-primary)",
        fontSize: "3.5rem",
        fontWeight: 800,
        marginBottom: "20px",
        lineHeight: 1.2,
        marginTop: "0",
        textAlign: "left",
      } as React.CSSProperties,
      description: {
        color: "var(--color-text-secondary)",
        fontSize: "1.25rem",
        lineHeight: 1.7,
        marginBottom: "40px",
        maxWidth: "500px",
        textAlign: "left",
      } as React.CSSProperties,
      formContainer: {
        backgroundColor: "var(--color-surface)",
        border: "2px solid var(--color-border)",
        borderRadius: "20px",
        padding: "40px",
        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
        marginTop: "0",
      } as React.CSSProperties,
      input: {
        backgroundColor: "var(--color-surface)",
        border: "2px solid var(--color-border)",
        color: "var(--color-text)",
        padding: "14px 18px",
        borderRadius: "12px",
        fontSize: "1rem",
        width: "100%",
        outline: "none",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        fontFamily: "inherit",
        textAlign: "left",
      } as React.CSSProperties,
      textarea: {
        backgroundColor: "var(--color-surface)",
        border: "2px solid var(--color-border)",
        color: "var(--color-text)",
        padding: "14px 18px",
        borderRadius: "12px",
        fontSize: "1rem",
        width: "100%",
        minHeight: "140px",
        outline: "none",
        transition: "border-color 0.3s ease, box-shadow 0.3s ease",
        fontFamily: "inherit",
        resize: "vertical" as "vertical",
        textAlign: "left",
      },
      label: {
        display: "block",
        color: "var(--color-text)",
        marginBottom: "10px",
        fontWeight: 600,
        fontSize: "0.95rem",
        letterSpacing: "0.3px",
        textAlign: "left",
      } as React.CSSProperties,
      formGroup: {
        marginBottom: "24px",
        textAlign: "left",
      } as React.CSSProperties,
      button: {
        background:
          "linear-gradient(to right, var(--color-primary), var(--color-accent))",
        border: "none",
        color: "white",
        fontWeight: 600,
        padding: "16px 36px",
        borderRadius: "12px",
        fontSize: "1.1rem",
        cursor: "pointer",
        transition: "all 0.3s ease",
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
      } as React.CSSProperties,
      buttonDisabled: {
        background:
          "linear-gradient(to right, var(--color-primary), var(--color-accent))",
        border: "none",
        color: "white",
        fontWeight: 600,
        padding: "16px 36px",
        borderRadius: "12px",
        fontSize: "1.1rem",
        cursor: "not-allowed",
        opacity: 0.7,
        display: "inline-flex",
        alignItems: "center",
        gap: "10px",
      } as React.CSSProperties,
      socialIconsContainer: {
        display: "flex",
        gap: "16px",
        marginTop: "40px",
        justifyContent: "flex-start",
      } as React.CSSProperties,
      socialIcon: {
        backgroundColor: isDarkMode
          ? "rgba(153, 27, 27, 0.1)"
          : "rgba(14, 165, 233, 0.1)",
        border: `2px solid ${
          isDarkMode ? "rgba(153, 27, 27, 0.2)" : "rgba(14, 165, 233, 0.2)"
        }`,
        color: "var(--color-primary)",
        width: "52px",
        height: "52px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        transition: "all 0.3s ease",
        textDecoration: "none",
      } as React.CSSProperties,
    };
  };

  const styles = getThemeStyles();

  return (
    <div style={styles.wrapper}>
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* Left Column */}
          <div style={styles.leftColumn}>
            <h1 style={styles.title}>Get In Touch</h1>
            <p style={styles.description}>
              Have questions about ASTRANOVA 2026? We're here to help! Send us a
              message and our team will respond within 24 hours.
            </p>

            <div style={styles.socialIconsContainer}>
              {socialIcons.map(({ Icon, href }, index) => (
                <a
                  key={index}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.socialIcon}
                  aria-label={`Follow us on ${
                    Icon.displayName || "social media"
                  }`}
                >
                  <Icon size={24} />
                </a>
              ))}
            </div>
          </div>

          {/* Right Column - Form */}
          <div>
            <div style={styles.formContainer}>
              <form onSubmit={handleSubmit}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="email">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ":focus": {
                        borderColor: "var(--color-primary)",
                        boxShadow:
                          "0 0 0 3px rgba(var(--color-primary-rgb), 0.1)",
                      },
                    }}
                    placeholder="youremail@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="name">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    style={{
                      ...styles.input,
                      ":focus": {
                        borderColor: "var(--color-primary)",
                        boxShadow:
                          "0 0 0 3px rgba(var(--color-primary-rgb), 0.1)",
                      },
                    }}
                    placeholder="John Doe"
                    required
                    disabled={isLoading}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="message">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    style={{
                      ...styles.textarea,
                      ":focus": {
                        borderColor: "var(--color-primary)",
                        boxShadow:
                          "0 0 0 3px rgba(var(--color-primary-rgb), 0.1)",
                      },
                    }}
                    placeholder="Tell us about your inquiry..."
                    required
                    disabled={isLoading}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    marginTop: "32px",
                  }}
                >
                  <button
                    type="submit"
                    disabled={isLoading}
                    style={
                      isLoading
                        ? styles.buttonDisabled
                        : {
                            ...styles.button,
                            ":hover": {
                              transform: "translateY(-3px)",
                              boxShadow:
                                "0 8px 25px rgba(var(--color-primary-rgb), 0.3)",
                            },
                            ":active": {
                              transform: "translateY(-1px)",
                            },
                          }
                    }
                  >
                    {isLoading ? (
                      <>
                        <div style={{ 
                          width: "20px", 
                          height: "20px", 
                          border: "2px solid white",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite"
                        }} />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Media query for responsive design */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          
          h1[style*="font-size: 3.5rem"] {
            font-size: 2.5rem !important;
          }
          
          div[style*="padding: 40px"] {
            padding: 30px !important;
          }
        }
        
        @media (max-width: 480px) {
          h1[style*="font-size: 3.5rem"] {
            font-size: 2rem !important;
          }
          
          div[style*="padding: 40px"] {
            padding: 24px !important;
          }
          
          button[style*="padding: 16px 36px"] {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
}

export default ContactUs;